/**
 * The account and subject lists' state, as it lives in the URL.
 *
 * Search, filters, sort and page are the administrator's working context, so
 * they are the address rather than component state: Back from an account
 * lands on the same filtered page, a narrowed list can be bookmarked or sent
 * to a colleague, and a reload keeps it.
 *
 * Lowercase words rather than the API's enums — `?role=tutor&status=disabled`
 * — because a URL is something people read and paste. Defaults are left out
 * of it entirely, so an unfiltered list is the bare path. Anything that does
 * not parse is dropped rather than rejected, the same rule the product's
 * lists follow: a stale link should open the list, not an error.
 */

export const LIST_PAGE_SIZE = 25

export const ACCOUNT_ROLES = ['tutor', 'student', 'admin'] as const
export const ACCOUNT_STATUSES = ['active', 'disabled'] as const
export const ACCOUNT_SORTS = ['newest', 'email', 'name'] as const

export interface AccountSearch {
  q?: string
  role?: (typeof ACCOUNT_ROLES)[number]
  status?: (typeof ACCOUNT_STATUSES)[number]
  /** `newest` is the default and never written. */
  sort?: Exclude<(typeof ACCOUNT_SORTS)[number], 'newest'>
  /** One-based. `1` is the default and never written. */
  page?: number
}

export const SUBJECT_STATUSES = ['active', 'inactive'] as const
export const SUBJECT_SORTS = ['code', 'newest'] as const

export interface SubjectSearch {
  q?: string
  status?: (typeof SUBJECT_STATUSES)[number]
  /** `code` is the default and never written. */
  sort?: Exclude<(typeof SUBJECT_SORTS)[number], 'code'>
  page?: number
}

function text(search: Record<string, unknown>, key: string): string | undefined {
  const value = search[key]
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed.slice(0, 120) : undefined
}

function oneOf<T extends string>(
  search: Record<string, unknown>,
  key: string,
  allowed: readonly T[],
): T | undefined {
  const value = search[key]
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined
}

function page(search: Record<string, unknown>): number | undefined {
  const value = Number(search['page'])
  return Number.isInteger(value) && value > 1 && value <= 4000 ? value : undefined
}

/** A change to a list's state: a key set to `undefined` removes that filter. */
export type SearchChange<T> = { [K in keyof T]?: T[K] | undefined }

/** Only the keys that carry something, so a default never reaches the address bar. */
function compact<T extends object>(value: SearchChange<T>): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T
}

export function parseAccountSearch(search: Record<string, unknown>): AccountSearch {
  const sort = oneOf(search, 'sort', ACCOUNT_SORTS)
  return compact<AccountSearch>({
    q: text(search, 'q'),
    role: oneOf(search, 'role', ACCOUNT_ROLES),
    status: oneOf(search, 'status', ACCOUNT_STATUSES),
    sort: sort === 'newest' ? undefined : sort,
    page: page(search),
  })
}

export function parseSubjectSearch(search: Record<string, unknown>): SubjectSearch {
  const sort = oneOf(search, 'sort', SUBJECT_SORTS)
  return compact<SubjectSearch>({
    q: text(search, 'q'),
    status: oneOf(search, 'status', SUBJECT_STATUSES),
    sort: sort === 'code' ? undefined : sort,
    page: page(search),
  })
}

/**
 * The next list state: `change` applied, back to the first page unless the
 * change is the page itself — a new filter over page four would otherwise
 * open on a page that may no longer exist.
 */
export function nextSearch<T extends { page?: number }>(current: T, change: SearchChange<T>): T {
  const merged: SearchChange<T> = { ...current, ...change }
  if (!('page' in change) || merged.page === 1) delete merged.page
  return compact<T>(merged)
}

/** Whether anything narrows the list. Sort and page do not: they order and slice it. */
export function isFiltered(search: { q?: string; role?: string; status?: string }): boolean {
  return Boolean(search.q || search.role || search.status)
}
